import { HttpErrorResponse } from '@angular/common/http';
import { isHttpTimeoutError } from '../../core/models/http-timeout-error';

export type LoadErrorKind = 'timeout' | 'offline' | 'server';

export interface LoadErrorCopy {
  kind: LoadErrorKind;
  title: string;
  body: string;
}

/**
 * Clinical-safety line, deliberately appended to every error class rather than just the generic
 * one. The failure this whole treatment exists to prevent is a failed read being read as "you have
 * no records", so a patient must be told their data is intact whether the request timed out, the
 * device is offline, or the API returned 500.
 */
const DATA_UNAFFECTED = 'Your data has not been affected.';

// The `server` wording is the copy agreed on PBI 53292 AC1; the other two lead with what the
// patient can act on before repeating the same reassurance.
const BODY_BY_KIND: Record<LoadErrorKind, string> = {
  server: `There was a problem retrieving this information. ${DATA_UNAFFECTED}`,
  timeout: `This is taking longer than expected. ${DATA_UNAFFECTED}`,
  offline: `You appear to be offline. Check your connection, then try again. ${DATA_UNAFFECTED}`,
};

/** Copy for the dismissible panel a blocking overlay shows once it outruns its budget. */
export interface StalledCopy {
  title: string;
  body: string;
  dismissLabel: string;
}

/**
 * Copy for a **save** that has outrun its overlay budget — kept here, beside the read wording, so the
 * two stay visibly different and cannot drift into each other.
 *
 * It deliberately does NOT reuse `BODY_BY_KIND.timeout`, and specifically not `DATA_UNAFFECTED`.
 * "Your data has not been affected" is a true and reassuring thing to say about a failed *read*; said
 * about a write that is still in flight it is simply a claim we cannot make — the server may be
 * committing it right now. Telling a patient their data is untouched and then showing them a changed
 * profile is worse than saying nothing.
 *
 * For the same reason there is no retry affordance: re-submitting an unbounded, uncancellable write
 * is how one save becomes two. The only honest action is to let the patient out of the overlay and
 * tell them to check rather than resubmit.
 */
export const SAVE_STALLED_COPY: StalledCopy = {
  title: 'This is taking longer than expected',
  body: 'Your changes may still be saving. Please do not submit again — reopen this page in a moment to check whether they were saved.',
  dismissLabel: 'Continue',
};

/**
 * The same panel for a flow that submits a *request* rather than saving a form — today the patient
 * assignment / identity verification POST.
 *
 * Separate wording because `SAVE_STALLED_COPY` tells the patient to reopen the page and check
 * whether their changes were saved. On verification there is nothing to reopen and check: success
 * routes them out of the flow entirely, so "reopen this page" reads as an instruction to do exactly
 * the resubmit the sentence before it forbids.
 */
export const VERIFICATION_STALLED_COPY: StalledCopy = {
  title: 'This is taking longer than expected',
  body: 'Your request may still be going through. Please do not submit it again — wait a moment, and we will take you to the next step if it completes.',
  dismissLabel: 'Continue',
};

export function classifyLoadError(error: unknown): LoadErrorKind {
  // Order matters. HttpTimeoutError carries status 0 (no response was received), so it has to be
  // matched before the network-down check or every client timeout would be reported as "offline".
  //
  // The `isTimeout` brand is checked alongside `instanceof` deliberately: if NgRx runtimeChecks or
  // devtools serialization is ever enabled, the class identity is lost in transit but the field
  // survives — without this, every timeout would silently reclassify as offline and no test would fail.
  if (isHttpTimeoutError(error) || (error as { isTimeout?: boolean })?.isTimeout === true) {
    return 'timeout';
  }

  if (error instanceof HttpErrorResponse && error.status === 0) {
    return 'offline';
  }

  return 'server';
}

/**
 * Builds the error-state copy for a failed read. `resourceLabel` is the lower-case noun phrase for
 * what failed to load ('your allergies', 'questionnaire history') and completes the title.
 */
export function getLoadErrorCopy(error: unknown, resourceLabel: string): LoadErrorCopy {
  const kind = classifyLoadError(error);

  return {
    kind,
    title: `Could not load ${resourceLabel}`,
    body: BODY_BY_KIND[kind],
  };
}
